import type { CurrentUser } from "@/lib/auth";
import {
  userIsAbrechnung,
  userIsAnrechnung,
  userIsBuero,
  userIsManagement,
} from "@/lib/auth";
import type { TimesheetSummary } from "@/lib/timesheets";
import type {
  TimesheetFilters,
  TimesheetStatusFilter,
  YearFilter,
} from "@/pages/stundennachweise/useTimesheetFilters";

export type TimesheetStatusKey =
  | "draft"
  | "ready"
  | "signed"
  | "archived"
  | "invoice_draft"
  | "invoice_created"
  | "invoice_paid"
  | string;

const BILLING_TAB_STATUSES = new Set<TimesheetStatusKey>([
  "signed",
  "invoice_draft",
  "invoice_created",
  "invoice_paid",
]);

/** Tab „Abrechnung & Rückfragen“ nur ab Unterschrieben / Rechnungsentwurf / Abgerechnet / Bezahlt. */
export function timesheetShowsBillingTab(status: string | null | undefined): boolean {
  return BILLING_TAB_STATUSES.has(normalizeTimesheetStatus(status));
}

export function normalizeTimesheetStatus(status: string | null | undefined): TimesheetStatusKey {
  const key = (status || "").toString().toLowerCase().trim();
  switch (key) {
    case "draft":
    case "ready":
    case "signed":
    case "archived":
      return key;
    case "invoice_draft":
    case "invoice-draft":
    case "invoice_draft_created":
    case "invoice-draft-created":
    case "rechnungsentwurf":
    case "rechnungsentwurf erstellt":
      return "invoice_draft";
    case "invoice_created":
    case "invoice-created":
    case "invoice":
    case "abgerechnet":
      return "invoice_created";
    case "invoice_paid":
    case "invoice-paid":
    case "paid":
      return "invoice_paid";
    default:
      return key || "draft";
  }
}

const TIMESHEET_STATUS_ORDER: TimesheetStatusKey[] = [
  "draft",
  "ready",
  "signed",
  "invoice_draft",
  "invoice_created",
  "invoice_paid",
  "archived",
];

function timesheetStatusRank(status: TimesheetStatusKey): number {
  const idx = TIMESHEET_STATUS_ORDER.indexOf(status);
  return idx >= 0 ? idx : -1;
}

/** Effektiven Status aus DB-Feld, API-Anzeige und Rechnungsentwurf-Dokument ableiten. */
export function resolveTimesheetRecordStatus(ts: {
  status?: string | null;
  status_display?: string | null;
  gd_rechnung_id?: unknown;
  invoice_customer_document_id?: unknown;
  invoice_document_id?: unknown;
}): TimesheetStatusKey {
  const hasInvoiceDraftDoc = Boolean(
    (ts.gd_rechnung_id != null && ts.gd_rechnung_id !== "")
      ? ts.gd_rechnung_id
      : ts.invoice_customer_document_id != null && ts.invoice_customer_document_id !== ""
        ? ts.invoice_customer_document_id
        : ts.invoice_document_id,
  );
  const dbStatus = normalizeTimesheetStatus(ts.status || "draft");
  const displayStatus = normalizeTimesheetStatus(ts.status_display || "");
  let resolved =
    timesheetStatusRank(displayStatus) > timesheetStatusRank(dbStatus) ? displayStatus : dbStatus;
  if (hasInvoiceDraftDoc && timesheetStatusRank(resolved) < timesheetStatusRank("invoice_draft")) {
    resolved = "invoice_draft";
  }
  // Verwaister Rechnungsentwurf (gelöscht, ohne Dokument-Link): wie „unterschrieben“.
  if (resolved === "invoice_draft" && !hasInvoiceDraftDoc) {
    resolved = dbStatus === "invoice_draft" ? "signed" : dbStatus;
  }
  return resolved;
}

export function getTimesheetStatus(ts: TimesheetSummary): TimesheetStatusKey {
  return resolveTimesheetRecordStatus(ts);
}

export function timesheetStatusLabel(status: string | null | undefined): string {
  const normalized = normalizeTimesheetStatus(status);
  switch (normalized) {
    case "draft":
      return "Entwurf";
    case "ready":
      return "Geschrieben";
    case "signed":
      return "Unterschrieben";
    case "archived":
      return "Archiviert";
    case "invoice_draft":
      return "Rechnungsentwurf";
    case "invoice_created":
      return "Abgerechnet";
    case "invoice_paid":
      return "Bezahlt";
    default:
      return status || "Entwurf";
  }
}

export function getTimesheetDisplayNumber(ts: TimesheetSummary): string {
  if (!ts) return "";
  return String(ts.display_number || ts.timesheet_number || "");
}

export function getTimesheetOrdnerNummer(ts: TimesheetSummary): string {
  const an = String(ts.auftragsnummer || "").trim();
  const ordner = an.split("#")[0] || "";
  const fallback = ts.ordnernummer || (ts.ordner_nummer as string | undefined) || "";
  return (ordner || fallback || "").toString();
}

export function normalizeTimesheetOrdnerBeschreibung(
  rawBeschreibung: string,
  ordnerNummer = "",
): string {
  const value = String(rawBeschreibung || "").trim();
  if (!value) return "";
  const nummer = String(ordnerNummer || "").trim();
  if (!nummer) return value;
  const escapedNummer = nummer.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const prefixPattern = new RegExp(`^${escapedNummer}\\s*#\\s*[^\\s]+\\s*[-–:]?\\s*`, "i");
  return value.replace(prefixPattern, "").trim();
}

export function getTimesheetOrdnerBeschreibung(ts: TimesheetSummary): string {
  const nummer = getTimesheetOrdnerNummer(ts);
  const raw = String(
    ts.ordner_beschreibung ||
      ts.ordnerbeschreibung ||
      ts.ordner_name ||
      ts.ordner_titel ||
      ts.folder_name ||
      ts.folder_title ||
      "",
  );
  return normalizeTimesheetOrdnerBeschreibung(raw, nummer);
}

export function getTimesheetKundenname(ts: TimesheetSummary): string {
  return String(
    ts.kunde ||
      ts.customer_name ||
      ts.kundenname ||
      ts.customer ||
      ts.auftrag_kunde ||
      "",
  );
}

export function isSTimesheet(ts: TimesheetSummary): boolean {
  return getTimesheetOrdnerNummer(ts).toUpperCase().startsWith("S");
}

export function getCreatedYearFromTimesheet(ts: TimesheetSummary): number | null {
  const iso = ts.created_at || (ts.created as string | undefined) || ts.period_start || null;
  if (iso) {
    const d = new Date(iso);
    if (!Number.isNaN(d.getTime())) return d.getFullYear();
  }
  const directYear = ts.jahr ?? ts.year;
  if (directYear != null) {
    const num = Number(directYear);
    if (!Number.isNaN(num)) return num;
  }
  return null;
}

export function getYearFromTimesheet(ts: TimesheetSummary): number | null {
  const directYear = ts.jahr ?? ts.year;
  if (directYear != null) {
    const num = Number(directYear);
    if (!Number.isNaN(num)) return num;
  }
  const iso = ts.created_at || (ts.created as string | undefined) || ts.period_start || null;
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.getFullYear();
}

export function matchesTimesheetSearch(ts: TimesheetSummary, searchTerm: string): boolean {
  if (!searchTerm) return true;
  const s = searchTerm.toLowerCase();
  const fields = [
    ts.title,
    ts.label,
    ts.project_name,
    ts.auftrag_display,
    ts.auftragsnummer,
    ts.ansprechpartner_nachname,
    getTimesheetOrdnerNummer(ts),
    getTimesheetKundenname(ts),
  ].filter(Boolean);
  return fields.some((f) => String(f).toLowerCase().includes(s));
}

export function hasBillingQuestions(ts: TimesheetSummary): boolean {
  if (!ts) return false;
  // Flag zuerst: Number(null) ist 0 und darf ein gesetztes Kennzeichen nicht schlucken.
  if (ts.has_billing_questions || ts.billing_questions_open) return true;
  const count = Number(ts.billing_question_count ?? ts.billing_questions_count);
  if (Number.isFinite(count) && count > 0) return true;
  return hasOpenBillingQuestions(ts);
}

export function hasOpenBillingQuestions(ts: TimesheetSummary): boolean {
  if (!ts) return false;
  if (ts.billing_questions_open) return true;
  const openCount = Number(ts.billing_question_open_count);
  return Number.isFinite(openCount) && openCount > 0;
}

export function shouldHighlightBillingQuestionRow(
  user: CurrentUser | null,
  ts: TimesheetSummary,
): boolean {
  return userIsManagement(user) && hasOpenBillingQuestions(ts);
}

export function getTimesheetYears(timesheets: TimesheetSummary[]): number[] {
  const years = new Set<number>();
  timesheets.forEach((ts) => {
    if (isSTimesheet(ts)) return;
    const y = getCreatedYearFromTimesheet(ts);
    if (y) years.add(y);
  });
  if (!years.size) years.add(new Date().getFullYear());
  return [...years].sort((a, b) => b - a);
}

export function ensureActiveYearFromData(
  timesheets: TimesheetSummary[],
  current: YearFilter,
): YearFilter {
  const hasNonS = timesheets.some((ts) => !isSTimesheet(ts));
  const hasS = timesheets.some(isSTimesheet);
  if (!hasNonS && hasS) return "S";
  if (current === "S" || current === "all") return current;
  const years = getTimesheetYears(timesheets);
  if (typeof current === "number" && !years.includes(current)) {
    return years[0] ?? new Date().getFullYear();
  }
  return current;
}

/**
 * Aktives Jahr anhand der (leichten) Server-Facetten korrigieren – Ersatz für
 * `ensureActiveYearFromData`, wenn nur das aktive Jahr geladen wird.
 */
export function ensureActiveYearFromFacets(
  facets: { years: number[]; hasS: boolean } | null,
  current: YearFilter,
): YearFilter {
  if (!facets) return current;
  if (current === "all") return current;
  if (current === "S") {
    if (!facets.hasS && facets.years.length) return facets.years[0] ?? current;
    return "S";
  }
  if (typeof current === "number" && facets.years.includes(current)) return current;
  if (facets.years.length) return facets.years[0] ?? current;
  if (facets.hasS) return "S";
  return current;
}

export interface OrdnerChip {
  nummer: string;
  beschreibung: string;
}

export function deriveKundenForYear(
  timesheets: TimesheetSummary[],
  jahr: number,
): string[] {
  const kundenSet = new Set<string>();
  timesheets.forEach((ts) => {
    if (isSTimesheet(ts)) return;
    const year = getCreatedYearFromTimesheet(ts);
    const kunde = getTimesheetKundenname(ts);
    if (Number(year) === Number(jahr) && kunde) kundenSet.add(kunde);
  });
  return [...kundenSet].sort((a, b) => a.localeCompare(b, "de"));
}

export function deriveOrdnerForYear(
  timesheets: TimesheetSummary[],
  jahr: number,
  kundeFilter: string,
): OrdnerChip[] {
  const ordnerMap = new Map<string, OrdnerChip>();
  timesheets.forEach((ts) => {
    if (isSTimesheet(ts)) return;
    const year = getCreatedYearFromTimesheet(ts);
    if (Number(year) !== Number(jahr)) return;
    const kunde = getTimesheetKundenname(ts);
    if (kundeFilter && kunde !== kundeFilter) return;
    const nummer = getTimesheetOrdnerNummer(ts);
    if (!nummer) return;
    const beschreibung = getTimesheetOrdnerBeschreibung(ts);
    const existing = ordnerMap.get(nummer);
    if (!existing) {
      ordnerMap.set(nummer, { nummer, beschreibung });
      return;
    }
    if (!existing.beschreibung && beschreibung) {
      existing.beschreibung = beschreibung;
    }
  });
  return [...ordnerMap.values()].sort((a, b) =>
    a.nummer.localeCompare(b.nummer, "de", { numeric: true }),
  );
}

export function deriveSOrdnerChips(timesheets: TimesheetSummary[]): OrdnerChip[] {
  const sOrdnerMap = new Map<string, OrdnerChip>();
  timesheets.forEach((ts) => {
    if (!isSTimesheet(ts)) return;
    const nummer = getTimesheetOrdnerNummer(ts);
    if (!nummer) return;
    const beschreibung = getTimesheetOrdnerBeschreibung(ts);
    const existing = sOrdnerMap.get(nummer);
    if (!existing) {
      sOrdnerMap.set(nummer, { nummer, beschreibung });
      return;
    }
    if (!existing.beschreibung && beschreibung) {
      existing.beschreibung = beschreibung;
    }
  });
  return [...sOrdnerMap.values()].sort((a, b) =>
    a.nummer.localeCompare(b.nummer, "de", { numeric: true }),
  );
}

export function filterTimesheets(
  timesheets: TimesheetSummary[],
  filters: TimesheetFilters,
  user: CurrentUser | null,
): TimesheetSummary[] {
  let data = [...timesheets].filter((ts) => !ts.has_newer_revision);

  const suche = (filters.q || "").trim().toLowerCase();
  const sucheAktiv = suche.length > 0;

  if (filters.jahr === "S") {
    data = data.filter(isSTimesheet);
    if (filters.sordner) {
      const target = filters.sordner.toUpperCase();
      data = data.filter((ts) => {
        const ordnerNum = getTimesheetOrdnerNummer(ts).toUpperCase();
        const an = String(ts.auftragsnummer || "").toUpperCase();
        return ordnerNum === target || an.startsWith(`${target}#`);
      });
    }
  } else if (typeof filters.jahr === "number") {
    data = data.filter((ts) => {
      const isS = isSTimesheet(ts);
      const year = getCreatedYearFromTimesheet(ts);
      return !isS && Number(year) === Number(filters.jahr);
    });
  }

  if (filters.status !== "all") {
    data = data.filter((ts) => getTimesheetStatus(ts) === filters.status);
  }

  if (filters.billing && filters.billing !== "all") {
    const target = filters.billing.trim().toLowerCase();
    data = data.filter((ts) => {
      const assignee = String(ts.billing_assignee_login || "")
        .trim()
        .toLowerCase();
      return assignee === target;
    });
  }

  if (filters.rueckfrage) {
    const canSeeQuestions =
      userIsAbrechnung(user) || userIsAnrechnung(user) || userIsManagement(user);
    // Offen (rot) bleibt. Beantwortet (grün) bleibt, solange der Nachweis nicht
    // abgerechnet oder bezahlt ist – dann verschwindet die Rückfrage aus der Liste.
    data = data.filter((ts) => canSeeQuestions && shouldShowBillingQuestionBadge(ts));
  }

  if (typeof filters.jahr === "number") {
    if (filters.ordner) {
      data = data.filter((ts) => getTimesheetOrdnerNummer(ts) === filters.ordner);
    } else if (filters.kunde) {
      data = data.filter((ts) => getTimesheetKundenname(ts) === filters.kunde);
    }
  }

  if (sucheAktiv) {
    data = data.filter((ts) => matchesTimesheetSearch(ts, suche));
  }

  if (sucheAktiv && filters.contactAgingSearch) {
    data = data.filter((ts) => getContactAgeBadgeConfig(ts) != null);
  }

  return data;
}

export const STATUS_FILTER_OPTIONS: { value: TimesheetStatusFilter; label: string }[] = [
  { value: "all", label: "Alle" },
  { value: "draft", label: "Entwürfe" },
  { value: "ready", label: "Geschrieben" },
  { value: "signed", label: "Unterschrieben" },
  { value: "archived", label: "Archiviert" },
];

export const BILLING_STATUS_OPTIONS: { value: TimesheetStatusFilter; label: string }[] = [
  { value: "invoice_draft", label: "Rechnungsentwurf" },
  { value: "invoice_created", label: "Abgerechnet" },
  { value: "invoice_paid", label: "Bezahlt" },
];

export interface ContactAgeThreshold {
  days: number;
  label: string;
  className: string;
}

export function getContactAgeThresholds(): ContactAgeThreshold[] {
  return [
    { days: 28, label: "> 4 Wochen", className: "bg-red-600 text-white" },
    { days: 21, label: "> 3 Wochen", className: "bg-orange-500 text-white" },
    { days: 14, label: "> 2 Wochen", className: "bg-amber-500 text-amber-950" },
    { days: 7, label: "> 1 Woche", className: "bg-yellow-400 text-yellow-950" },
  ];
}

export type TimesheetContactAgeSource = Pick<
  TimesheetSummary,
  "at_contact" | "at_contact_at" | "status" | "status_display"
>;

export function getContactAgeInfo(ts: TimesheetContactAgeSource, now = Date.now()) {
  if (!ts?.at_contact) return null;
  const status = normalizeTimesheetStatus(ts.status_display || ts.status || "draft");
  if (status !== "ready") return null;
  const contactMarkedAt = ts.at_contact_at ? new Date(ts.at_contact_at) : null;
  if (!contactMarkedAt || Number.isNaN(contactMarkedAt.getTime())) return null;
  const ageDays = (now - contactMarkedAt.getTime()) / (24 * 60 * 60 * 1000);
  const thresholds = getContactAgeThresholds();
  const threshold = thresholds.find((entry) => ageDays > entry.days) ?? null;
  if (!threshold) return null;
  return { ageDays, threshold };
}

export function getContactAgeBadgeConfig(ts: TimesheetContactAgeSource, now = Date.now()) {
  return getContactAgeInfo(ts, now)?.threshold ?? null;
}

/** PDF-URL für E-Mail-Versand (Entwurf vs. unterschrieben), analog zu stundennachweise.js. */
export function resolveTimesheetMailPdfUrl(
  ts: {
    status?: string | null;
    status_display?: string | null;
    invoice_customer_document_id?: unknown;
    invoice_document_id?: unknown;
  },
  links: { draft_url?: string; signed_url?: string },
  options?: { reminder?: boolean },
): string {
  if (options?.reminder) {
    if (!links.draft_url) throw new Error("Kein öffentlicher PDF-Link verfügbar.");
    return links.draft_url;
  }

  const status = resolveTimesheetRecordStatus(ts);
  const useSigned =
    status === "signed" ||
    status === "invoice_draft" ||
    status === "invoice_created" ||
    status === "invoice_paid";
  const url = useSigned ? links.signed_url || links.draft_url : links.draft_url;
  if (!url) throw new Error("Kein öffentlicher PDF-Link verfügbar.");
  return url;
}

export function getTimesheetContactName(ts: TimesheetSummary): string {
  const lastName = String(ts.ansprechpartner_nachname || "").trim();
  const firstName = String(ts.ansprechpartner_vorname || "").trim();
  const fallback = String(ts.ansprechpartner || ts.contact_name || "").trim();
  if (lastName && firstName) return `${lastName}, ${firstName}`;
  if (lastName) return lastName;
  if (firstName) return firstName;
  return fallback || "Unbekannt";
}

export function getTimesheetContactSearchTerm(ts: TimesheetSummary): string {
  const lastName = String(ts.ansprechpartner_nachname || "").trim();
  if (lastName) return lastName;
  const fallback = String(ts.ansprechpartner || ts.contact_name || "").trim();
  if (fallback) return fallback;
  return String(ts.ansprechpartner_vorname || "").trim();
}

export function formatInvoiceValue(value: unknown): string {
  if (value == null || value === "") return "—";
  const num = Number(value);
  if (Number.isNaN(num)) return String(value);
  return new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
}

export function statusBadgeClass(
  status: string | null | undefined,
  user: CurrentUser | null,
): string {
  const normalized = normalizeTimesheetStatus(status);
  const isBuero = userIsBuero(user);
  const isAbrechnung = userIsAbrechnung(user);

  switch (normalized) {
    case "draft":
      return "bg-fill-tertiary text-label-secondary";
    case "ready":
      return "bg-amber-400 text-amber-950";
    case "signed":
      return "bg-green-600 text-white";
    case "invoice_draft":
      return isBuero || isAbrechnung
        ? "bg-violet-600 text-white"
        : "bg-fill-tertiary text-label-secondary";
    case "invoice_created":
    case "invoice_paid":
      return isBuero || isAbrechnung ? "bg-emerald-900 text-white" : "bg-green-600 text-white";
    case "archived":
      return "bg-fill-tertiary text-label-secondary";
    default:
      return "bg-fill-tertiary text-label-secondary";
  }
}

const SETTLED_BILLING_STATUSES = new Set<TimesheetStatusKey>(["invoice_created", "invoice_paid"]);

/** Abgerechnet oder bezahlt: eine beantwortete Rückfrage ist erledigt. */
export function isSettledBillingTimesheet(ts: TimesheetSummary): boolean {
  return SETTLED_BILLING_STATUSES.has(normalizeTimesheetStatus(getTimesheetStatus(ts)));
}

/**
 * Grünes Rückfrage-Badge: alle Fragen beantwortet, Nachweis noch nicht
 * abgerechnet oder bezahlt.
 */
export function showsAnsweredBillingQuestionBadge(ts: TimesheetSummary): boolean {
  if (!hasBillingQuestions(ts) || hasOpenBillingQuestions(ts)) return false;
  return !isSettledBillingTimesheet(ts);
}

/** Badge „Rückfrage“: offen immer, beantwortet nur vor Abgerechnet/Bezahlt. */
export function shouldShowBillingQuestionBadge(ts: TimesheetSummary): boolean {
  if (!hasBillingQuestions(ts)) return false;
  if (hasOpenBillingQuestions(ts)) return true;
  return !isSettledBillingTimesheet(ts);
}

/** Rot = offene Rückfrage, Grün = alle beantwortet. */
export function getBillingQuestionBadgeClass(
  ts: TimesheetSummary,
  _isManagement?: boolean,
): string | null {
  if (!shouldShowBillingQuestionBadge(ts)) return null;
  if (hasOpenBillingQuestions(ts)) return "bg-status-red text-white";
  return "bg-status-green text-white";
}

/** Beantwortete Rückfragen, die Abrechnung noch nicht gesehen hat. */
export function hasUnreadBillingAnswers(ts: TimesheetSummary): boolean {
  if (!hasBillingQuestions(ts)) return false;
  if (hasOpenBillingQuestions(ts)) return false;
  const answeredAt = ts.billing_last_answered_at;
  if (!answeredAt) return false;
  const seenAt = ts.billing_seen_at;
  if (!seenAt) return true;
  return new Date(answeredAt).getTime() > new Date(seenAt).getTime();
}
